import { createContext, useState, useEffect, useContext } from 'react';
import api from '../services/api';

export const TenantContext = createContext(null);

// Slug resolution order:
// 1. subdomain  e.g. demo.trainaxis.com
// 2. ?club= query param
// 3. localStorage (set on login / manual entry)
const detectSlug = () => {
  const hostname = window.location.hostname;
  const parts    = hostname.split('.');
  // 1. Subdomain always wins
  const knownHosts = ['vercel.app', 'railway.app', 'netlify.app', 'localhost'];
const isKnownHost = knownHosts.some(h => hostname.includes(h));
if (!isKnownHost && parts.length >= 3 && !['www', 'api', 'app', 'admin'].includes(parts[0])) {
  return parts[0];
}
  // 2. Explicit ?club= query param
  const params = new URLSearchParams(window.location.search);
  if (params.get('club')) return params.get('club');
  // 3. On /login with no explicit hint → always show workspace finder, never auto-load
  if (window.location.pathname === '/login') return null;
  return localStorage.getItem('trainaxis_club_slug') || null;
};

// Super admin context: admin subdomain, ?superadmin=1, or /superadmin/* path
export const isSuperAdminHost = () => {
  const hostname = window.location.hostname;
  const isSASubdomain = hostname === 'admin.trainaxis.com' || hostname.startsWith('admin.');
  const isSAQuery    = new URLSearchParams(window.location.search).get('superadmin') === '1';
  const isSAPath     = window.location.pathname.startsWith('/superadmin');
  return isSASubdomain || isSAQuery || isSAPath;
};

export function TenantProvider({ children }) {
  const [club,    setClub]    = useState(null);
  const [slug,    setSlug]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const fetchClub = async (resolvedSlug) => {
    try {
      const res = await api.get(`/api/clubs/by-slug/${resolvedSlug}`);
      setClub(res.data.club);
      setError(null);
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'CLUB_SUSPENDED' || code === 'CLUB_CANCELLED') {
        setError(code);
      }
    }
  };

  useEffect(() => {
    // Super admin context — skip club resolution
    if (isSuperAdminHost()) {
      setLoading(false);
      return;
    }

    const resolved = detectSlug();
    if (!resolved) {
      setLoading(false);
      return;
    }

    setSlug(resolved);
    localStorage.setItem('trainaxis_club_slug', resolved);

    fetchClub(resolved).finally(() => setLoading(false));
  }, []);

  // Called by Login / AuthContext after successful login or manual slug entry
  const setTenantSlug = async (newSlug) => {
    if (!newSlug) return;
    localStorage.setItem('trainaxis_club_slug', newSlug);
    setSlug(newSlug);
    await fetchClub(newSlug);
  };

  // Called after login when we already have club data — avoids extra API round-trip
  const setClubDirectly = (clubData, clubSlug) => {
    setClub(clubData);
    setSlug(clubSlug);
    setError(null);
    if (clubSlug) localStorage.setItem('trainaxis_club_slug', clubSlug);
  };

  const clearTenant = () => {
    setClub(null);
    setSlug(null);
    setError(null);
    localStorage.removeItem('trainaxis_club_slug');
  };

  return (
    <TenantContext.Provider value={{ club, slug, loading, error, setTenantSlug, setClubDirectly, clearTenant }}>
      {children}
    </TenantContext.Provider>
  );
}

export const useTenant = () => useContext(TenantContext);
